import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Search, Plus, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'react-hot-toast';

const IntercompanyBillingPage = () => {
  const [billings, setBillings] = useState([]);
  const [filteredBillings, setFilteredBillings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [newBilling, setNewBilling] = useState({
    selling_company: '',
    buying_company: '',
    billing_date: new Date().toISOString().split('T')[0],
    reference_document: '',
    currency: 'USD',
    created_by: 'USER',
    items: [
      { item_number: 10, material_id: '', description: '', quantity: 0, unit_of_measure: 'EA', transfer_price: 0 }
    ]
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchBillings = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/intercompany/billings`);
      const data = await response.json();
      setBillings(data);
    } catch (error) {
      toast.error('Failed to load IC billings');
    }
  }, [backendUrl]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/company/companies`);
      if (!response.ok) {
        setCompanies([]);
        return;
      }
      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load companies');
      setCompanies([]);
    }
  }, [backendUrl]);

  const filterBillings = useCallback(() => {
    let filtered = billings;

    if (searchTerm) {
      filtered = filtered.filter(billing => 
        billing.ic_billing_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        billing.reference_document?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (companyFilter !== 'all') {
      filtered = filtered.filter(billing => 
        billing.selling_company === companyFilter || billing.buying_company === companyFilter
      );
    }

    setFilteredBillings(filtered);
  }, [billings, searchTerm, companyFilter]);

  useEffect(() => {
    fetchBillings();
    fetchCompanies();
  }, [fetchBillings, fetchCompanies]);

  useEffect(() => {
    filterBillings();
  }, [filterBillings]);

  const addItem = () => {
    const nextItemNum = (newBilling.items.length + 1) * 10;
    setNewBilling({
      ...newBilling,
      items: [
        ...newBilling.items,
        { item_number: nextItemNum, material_id: '', description: '', quantity: 0, unit_of_measure: 'EA', transfer_price: 0 }
      ]
    });
  };

  const removeItem = (index) => {
    const updatedItems = newBilling.items.filter((_, i) => i !== index);
    setNewBilling({ ...newBilling, items: updatedItems });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newBilling.items];
    updatedItems[index][field] = ['quantity', 'transfer_price'].includes(field) ? parseFloat(value) || 0 : value;
    setNewBilling({ ...newBilling, items: updatedItems });
  };

  const handleCreateBilling = async () => {
    if (!newBilling.selling_company || !newBilling.buying_company) {
      toast.error('Please select both selling and buying companies');
      return;
    }

    if (newBilling.selling_company === newBilling.buying_company) {
      toast.error('Selling and buying companies must be different');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/intercompany/billings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBilling)
      });

      if (response.ok) {
        toast.success('IC billing created successfully');
        setShowCreateDialog(false);
        fetchBillings();
        setNewBilling({
          selling_company: '',
          buying_company: '',
          billing_date: new Date().toISOString().split('T')[0],
          reference_document: '',
          currency: 'USD',
          created_by: 'USER',
          items: [
            { item_number: 10, material_id: '', description: '', quantity: 0, unit_of_measure: 'EA', transfer_price: 0 }
          ]
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create IC billing');
      }
    } catch (error) {
      toast.error('Error creating IC billing');
    }
  };

  const fetchReconciliation = async (companyCode) => {
    try {
      const response = await fetch(`${backendUrl}/api/intercompany/reconciliation/${companyCode}`);
      const data = await response.json();
      setReconciliationData(data);
      setShowReconciliation(true);
    } catch (error) {
      toast.error('Failed to load reconciliation data');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const totalValue = newBilling.items.reduce((sum, item) => sum + (item.quantity * item.transfer_price), 0);

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Intercompany Billing</h1>
            <p className="text-gray-600">Manage cross-company transactions</p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedCompany} onValueChange={(value) => {
              setSelectedCompany(value);
              if (value) fetchReconciliation(value);
            }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="View Reconciliation" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(company => (
                  <SelectItem key={company.company_id} value={company.company_id}>
                    {company.company_id} - {company.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create IC Billing
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Intercompany Billing</DialogTitle>
                  <DialogDescription>
                    Post a cross-company billing document. An AR document is created in the selling
                    company and an AP document in the buying company.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Selling Company *</label>
                      <Select value={newBilling.selling_company} onValueChange={(value) => setNewBilling({...newBilling, selling_company: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map(company => (
                            <SelectItem key={company.company_id} value={company.company_id}>
                              {company.company_id} - {company.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Buying Company *</label>
                      <Select value={newBilling.buying_company} onValueChange={(value) => setNewBilling({...newBilling, buying_company: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map(company => (
                            <SelectItem key={company.company_id} value={company.company_id}>
                              {company.company_id} - {company.company_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Billing Date *</label>
                      <Input
                        type="date"
                        value={newBilling.billing_date}
                        onChange={(e) => setNewBilling({...newBilling, billing_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Reference Document</label>
                      <Input
                        value={newBilling.reference_document}
                        onChange={(e) => setNewBilling({...newBilling, reference_document: e.target.value})}
                        placeholder="Optional reference"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Currency</label>
                      <Input
                        value={newBilling.currency}
                        onChange={(e) => setNewBilling({...newBilling, currency: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold">Items</h3>
                      <div className="text-sm font-semibold">
                        Total Value: {formatCurrency(totalValue)}
                      </div>
                    </div>

                    {newBilling.items.map((item, index) => (
                      <div key={index} className="border rounded p-3 mb-3 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-sm">Item {item.item_number}</span>
                          {newBilling.items.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => removeItem(index)}>
                              ×
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          <div>
                            <label className="block text-xs mb-1">Material ID</label>
                            <Input
                              value={item.material_id}
                              onChange={(e) => updateItem(index, 'material_id', e.target.value)}
                              placeholder="Material"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs mb-1">Description *</label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              placeholder="Item description"
                            />
                          </div>
                          <div>
                            <label className="block text-xs mb-1">Quantity *</label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs mb-1">Transfer Price *</label>
                            <Input
                              type="number"
                              value={item.transfer_price}
                              onChange={(e) => updateItem(index, 'transfer_price', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <Button variant="outline" onClick={addItem} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>

                  <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateBilling}>Create IC Billing</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by IC billing or reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.company_id} value={company.company_id}>
                    {company.company_id} - {company.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Billings Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>IC Billing Number</TableHead>
                <TableHead>Selling Company</TableHead>
                <TableHead>Buying Company</TableHead>
                <TableHead>Billing Date</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>AR Doc</TableHead>
                <TableHead>AP Doc</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBillings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <div>No intercompany billings found</div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBillings.map((billing) => (
                  <TableRow key={billing.ic_billing_number} data-testid={`ic-billing-row-${billing.ic_billing_number}`}>
                    <TableCell className="font-medium">{billing.ic_billing_number}</TableCell>
                    <TableCell>{billing.selling_company}</TableCell>
                    <TableCell>{billing.buying_company}</TableCell>
                    <TableCell>{billing.billing_date}</TableCell>
                    <TableCell>{billing.reference_document || '-'}</TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`ic-ar-doc-${billing.ic_billing_number}`}>{billing.ar_document || '-'}</TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`ic-ap-doc-${billing.ic_billing_number}`}>{billing.ap_document || '-'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(billing.total_value)}</TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800">{billing.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Reconciliation Dialog */}
        <Dialog open={showReconciliation} onOpenChange={setShowReconciliation}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>IC Reconciliation - {reconciliationData?.company_code}</DialogTitle>
              <DialogDescription>
                Intercompany receivables, payables and net position for the selected company.
              </DialogDescription>
            </DialogHeader>
            {reconciliationData && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                        Total Receivables
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(reconciliationData.total_receivables)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {reconciliationData.receivable_count} documents
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center">
                        <TrendingDown className="w-4 h-4 mr-2 text-red-600" />
                        Total Payables
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(reconciliationData.total_payables)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {reconciliationData.payable_count} documents
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Net Position</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-3xl font-bold ${reconciliationData.net_position >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(reconciliationData.net_position)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {reconciliationData.net_position >= 0 ? 'Net Receivable' : 'Net Payable'}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredBillings.length} of {billings.length} IC billings
        </div>
      </div>
    </Layout>
  );
};

export default IntercompanyBillingPage;